import type { Organizer } from '@/types';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { fetchOrganizerProfileVisibility } from '@/components/_shared/contexts/fetchers/fetchOrganizerProfile';

export type OrganizerProfileContextType = {
  hideAttendeeCount: boolean;
  hideTotalEvents: boolean;
  loading: boolean;
};

export const OrganizerProfileContext = createContext<OrganizerProfileContextType | null>(null);

const DEFAULT_VISIBILITY: OrganizerProfileContextType = {
  hideAttendeeCount: false,
  hideTotalEvents: false,
  loading: false,
};

type VisibilitySetters = {
  setHideAttendeeCount: React.Dispatch<React.SetStateAction<boolean>>;
  setHideTotalEvents: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

function resetVisibilityToDefaults({ setHideAttendeeCount, setHideTotalEvents, setLoading }: VisibilitySetters) {
  setHideAttendeeCount(false);
  setHideTotalEvents(false);
  setLoading(false);
}

function applyFetchedVisibility(
  data: { hideAttendeeCount: boolean; hideTotalEvents: boolean },
  { setHideAttendeeCount, setHideTotalEvents }: VisibilitySetters,
) {
  setHideAttendeeCount(data.hideAttendeeCount);
  setHideTotalEvents(data.hideTotalEvents);
}

async function loadOrganizerProfileVisibility(
  organizerId: string,
  cancelled: () => boolean,
  setters: VisibilitySetters,
) {
  const { setLoading } = setters;
  setLoading(true);
  try {
    const data = await fetchOrganizerProfileVisibility(organizerId);
    if (!cancelled()) {
      applyFetchedVisibility(data, setters);
    }
  } catch (error) {
    console.error('Failed to fetch organizer profile visibility:', error);
    if (!cancelled()) {
      setters.setHideAttendeeCount(false);
      setters.setHideTotalEvents(false);
    }
  } finally {
    if (!cancelled()) {
      setLoading(false);
    }
  }
}

function useOrganizerProfileState(organizer: Organizer | null | undefined): OrganizerProfileContextType {
  const organizerIdPresent = Boolean(organizer?.id?.trim());
  const [hideAttendeeCount, setHideAttendeeCount] = useState<boolean>(false);
  const [hideTotalEvents, setHideTotalEvents] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(organizerIdPresent);

  useEffect(() => {
    const organizerId = organizer?.id?.trim();

    if (!organizerId) {
      resetVisibilityToDefaults({
        setHideAttendeeCount,
        setHideTotalEvents,
        setLoading,
      });
      return;
    }

    let cancelled = false;
    void loadOrganizerProfileVisibility(organizerId, () => cancelled, {
      setHideAttendeeCount,
      setHideTotalEvents,
      setLoading,
    });

    return () => {
      cancelled = true;
    };
  }, [organizer?.id, setHideAttendeeCount, setHideTotalEvents, setLoading]);

  return {
    hideAttendeeCount,
    hideTotalEvents,
    loading,
  };
}

export const OrganizerProfileProvider: React.FC<{
  children: React.ReactNode;
  organizer: Organizer | null | undefined;
}> = ({ children, organizer }) => {
  const contextValue = useOrganizerProfileState(organizer);

  return <OrganizerProfileContext.Provider value={contextValue}>{children}</OrganizerProfileContext.Provider>;
};

export const useOrganizerProfile = (): OrganizerProfileContextType => {
  const context = useContext(OrganizerProfileContext);
  if (!context) {
    throw new Error('useOrganizerProfile must be used within an OrganizerProfileProvider');
  }
  return context;
};

export const useOrganizerProfileOptional = (): OrganizerProfileContextType => {
  return useContext(OrganizerProfileContext) ?? DEFAULT_VISIBILITY;
};
